import React, { useEffect } from 'react'
import { styled } from '@mui/material/styles'
import clsx from 'clsx'
import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import Drawer from '@mui/material/Drawer'
import routes from '../../constants/Routes'
import theme, { Theme } from 'theme'
import Header from './Header/Header'
import * as util from '../../util/bellplus-utils'
import Sidebar from './Sidebar/Sidebar'
import AppRouter from '../../router/AppRouter'
import Breadcrumb from '../../components/Breadcrumb'
import { useNavigate } from 'react-router-dom'

const PREFIX = 'DashboardLayout'

const classes = {
  dashboardContainer: `${PREFIX}-dashboardContainer`,
  headerContainer: `${PREFIX}-headerContainer`,
  headerContainerDesktop: `${PREFIX}-headerContainerDesktop`,
  headerContainerDesktopDrawerCollapsed: `${PREFIX}-headerContainerDesktopDrawerCollapsed`,
  sidebarContainer: `${PREFIX}-sidebarContainer`,
  sidebarContainerMobile: `${PREFIX}-sidebarContainerMobile`,
  sidebarContainerDesktop: `${PREFIX}-sidebarContainerDesktop`,
  sidebarContainerDesktopDrawerCollapsed: `${PREFIX}-sidebarContainerDesktopDrawerCollapsed`,
  drawer: `${PREFIX}-drawer`,
  drawerMobile: `${PREFIX}-drawerMobile`,
  drawerDesktop: `${PREFIX}-drawerDesktop`,
  drawerDesktopCollapsed: `${PREFIX}-drawerDesktopCollapsed`,
  headerSpacer: `${PREFIX}-headerSpacer`,
  content: `${PREFIX}-content`,
  paper: `${PREFIX}-paper`,
  fixedHeight: `${PREFIX}-fixedHeight`,
}

const Root = styled('div')(({ theme: Theme }) => ({
  [`&.${classes.dashboardContainer}`]: {
    display: 'flex',
    background: 'white', // Sales Dashboard,
  },

  [`& .${classes.headerContainer}`]: {
    top: 0,
    left: 0,
    right: 0,
    position: 'absolute',
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },

  [`& .${classes.headerContainerDesktop}`]: {
    left: 'auto',
    width: `100%`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),

  },

  [`& .${classes.headerContainerDesktopDrawerCollapsed}`]: {
    width: `calc(100% - ${theme.sidebar.widthCollapsed}px)`,
  },

  [`& .${classes.sidebarContainer}`]: {
    position: 'relative',
    [theme.breakpoints.up('md')]: {
      width: theme.sidebar.width,
      flexShrink: 0,
    },
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },

  [`& .${classes.sidebarContainerMobile}`]: {
    width: 10,
    marginTop: 80,
  },

  [`& .${classes.sidebarContainerDesktop}`]: {
    width: theme.sidebar.width,
    transition: '.5s ease',
    zIndex: 0,
    marginTop: '3.7%',
  },

  [`& .${classes.sidebarContainerDesktopDrawerCollapsed}`]: {
    overflowX: 'hidden',
    // width: theme.spacing(7) + 1,
    width: '55px',
    transition: '.5s ease',
    [theme.breakpoints.up('sm')]: {
      width: theme.spacing(9) + 1,
    },
  },

  [`& .${classes.drawer}`]: {
    flexShrink: 0,
  },

  [`& .${classes.drawerMobile}`]: {
    //width: '40%',
  },

  [`& .${classes.drawerDesktop}`]: {
    width: '100%',
    position: 'absolute',
  },

  [`& .${classes.drawerDesktopCollapsed}`]: {
    overflowX: 'hidden',
    // width: theme.spacing(7) + 1,
    width: '200px',
    [theme.breakpoints.up('sm')]: {
      width: theme.spacing(9) + 1,
    },
  },

  [`& .${classes.headerSpacer}`]: theme.mixins.toolbar,

  [`& .${classes.content}`]: {
	flexGrow: 1,
	    height: '100vh',
	    overflow: 'auto',
	    flexDirection: 'column',
	    display: 'flex',
	    // padding: 15,
	    background: '#f5f6f8',
  },

  [`& .${classes.paper}`]: {
    padding: theme.spacing(2),
    display: 'flex',
    overflow: 'auto',
    flexDirection: 'column',
  },

  [`& .${classes.fixedHeight}`]: {
    height: 240,
  },
}))

export interface DashboardProps {
  children?: any
}

export default function Dashboard({ children }: DashboardProps) {
  const navigate = useNavigate()
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))
  const isMobile = !isDesktop

  const [isSidebarOpenMobile, setIsSidebarOpenMobile] = React.useState(false)
  const [isSidebarCollapsedDesktop, setIsSidebarCollapsedDesktop] = React.useState(true)

  function handleSidebarMobileToggle() {
    setIsSidebarOpenMobile(!isSidebarOpenMobile)
  }

  function handleSidebarToggle() {
    // Open/close on mobile
    if (isMobile) {
      setIsSidebarOpenMobile(!isSidebarOpenMobile)
    }
    // Collapse/uncollapse on desktop
    else {
      setIsSidebarCollapsedDesktop(!isSidebarCollapsedDesktop)
    }
  }

  if (!util.isAuthenticated()) {
    util.clearSessionData()
    window.location.replace(routes.LOGIN.path);
    return <Root></Root>
  } else {
    return (
      <Root className={classes.dashboardContainer}>
        <Header onToggle={handleSidebarToggle} />
        {/* <div
        className={clsx(
          classes.headerContainer,
          isDesktop && classes.headerContainerDesktop,
          isDesktop &&
            isSidebarCollapsedDesktop &&
            classes.headerContainerDesktopDrawerCollapsed,
        )}
      >

      </div> */}

        <div
          className={clsx(
            classes.sidebarContainer,
            isMobile && classes.sidebarContainerMobile,
            isDesktop && classes.sidebarContainerDesktop,
            isDesktop &&
              isSidebarCollapsedDesktop &&
              classes.sidebarContainerDesktopDrawerCollapsed,
          )}
        >
          <Drawer
            variant="temporary"
            anchor={theme.direction === 'rtl' ? 'right' : 'left'}
            open={isSidebarOpenMobile}
            onClose={handleSidebarMobileToggle}
            classes={{
              paper: clsx(classes.drawer, classes.drawerMobile),
            }}
            ModalProps={{
              keepMounted: true, // Better open performance on mobile.
            }}
          >
            <Sidebar
              isDesktop={isDesktop}
              isMobile={isMobile}
              isSidebarCollapsedDesktop={isSidebarCollapsedDesktop}
              isSidebarOpenMobile={isSidebarOpenMobile}
            />
          </Drawer>
          <Drawer
            classes={{
              paper: clsx(
                classes.drawer,
                classes.drawerDesktop,
                isSidebarCollapsedDesktop && classes.drawerDesktopCollapsed,
              ),
            }}
            variant="permanent"
          >
            <Sidebar
              isDesktop={isDesktop}
              isMobile={isMobile}
              isSidebarCollapsedDesktop={isSidebarCollapsedDesktop}
              isSidebarOpenMobile={isSidebarOpenMobile}
            />
          </Drawer>
        </div>

        <main className={classes.content}>
          <div className={classes.headerSpacer} />
         <Breadcrumb />
          <AppRouter />
          {/* <Footer /> */}
        </main>
      </Root>
    )
  }
}

